import { select } from 'd3-selection';
import { timeDay } from 'd3-time';

import { consts, internalUnits } from '../utils/consts';
import { glucoseAccessor, idKey } from '../utils/dataUtils';
import { parseDate } from '../utils/formats';
import { getPathPoints, getSlowTransition, glucoseScale, parseTransform, remove } from '../utils/svgUtils';
import './Dashboard.css';


// TODO: check ranges from a Context...
const { hyper, high, low, hypo } = consts[internalUnits].ticks;

function getRange(d) {
  const value = glucoseAccessor(d);
  if (isNaN(value)) {
    return 'invalid';
  }
  if (value >= hyper) {
    return 'hyper';
  }
  if (value >= high) {
    return 'high';
  }
  if (value >= low) {
    return 'on-target';
  }
  if (value > hypo) {
    return 'low';
  }
  if (value <= hypo) {
    return 'hypo';
  }
  throw new Error(`Unable to select range for value ${value} from ${JSON.stringify(d)}`);
};

function getGlucoseClass(d) {
  return `${
    d.source ? `${d.source} ` : ''
    }${
    getRange(d)
    } glucose`;
}

function getSgvClass(d) {
  return `sgv ${getGlucoseClass(d)}`;
}

function getSmbgClass(d) {
  return `smbg ${getGlucoseClass(d)}`;
}

function getCircleYfromPathpoints(pathPoints, translate, timeScale, defaultGlucoseScale) {
  return function(d) {
    const x = Math.round(timeScale(d) + translate);
    return pathPoints[x] || defaultGlucoseScale(d);
  };
}

export function alignCirclesToPoints(selection, pathPoints, t, r = 2) {
  const circlesG = selection.select('g.sensor.circles');
  if (!circlesG.empty()) {
    const circlesTransform = parseTransform(circlesG.attr('transform'));
    let circles = circlesG.selectAll('circle');
    if (t) {
      circles = circles.transition(t);
    }
    circles.attr('cy', function(d) {
        const x = Math.round(this.getBBox().x + circlesTransform.translateX);
        return pathPoints[x];
      })
      .attr('r', r)
      .on('interrupt end', function() {
        selection.select('.sensor.circles').remove();
      });
  }
}

function render(selection) {
  const {
    data = {},
    day,
    days = [],
    formatGlucose,
    timeScale,
    width = 720,
  } = selection.datum();

  const {sgv = [], smbg = []} = data;

  if (!day && !days.length) {
    return;
  }

  const translateX = (day || days.length) ? timeScale({ time: parseDate(day || days[0]).toISOString() }) : 0;
  const t = getSlowTransition();

  const graphsG = selection
    .selectAll('g.graphs')
    .data([1], Object)
    .join('g')
    .attr('class', 'graphs');

  const previousDays = graphsG.selectAll('g.days');

  // check whether sensor circles already exist
  let pathPoints;
  const sensorLine = graphsG.select('path.sensor.line');
  if (!sensorLine.empty()) {
    pathPoints = getPathPoints(sensorLine.node());
  }

  // remove pending transitions
  graphsG.selectAll('g.days.exiting').remove();

  const dayGraphsG = graphsG.selectAll('g.days')
    .data(
      [{ timeScale, date: days.length ? parseDate(days[0]) : new Date(day) }],
      s => `${s.date} ${s.timeScale({ time: s.date.toISOString() })}`
    );

  dayGraphsG.exit()
    .classed('exiting', true);
  
  const dayGraphsGEnter = dayGraphsG.enter().append('g')
    .attr('class', 'days')
    .attr('transform', d => {
      let refTranslate;
      previousDays.each((d) => {
        if (d.timeScale) {
         const date = days.length ? parseDate(days[0]) : new Date(day);
          refTranslate = d.date < date
          ? timeScale({ time: timeDay.offset(date, 1).toISOString() })
          : d.timeScale({ time: date.toISOString() });
        }
      });
      return `translate(${!isNaN(refTranslate) ? refTranslate : -translateX},0)`;
    });
  
  const merged = dayGraphsG.merge(dayGraphsGEnter);
  merged.classed('transitioning', true)
    .transition(t)
      .attr('transform', `translate(${-translateX},0)`)
    .on('interrupt end', function(d) {
      const me = select(this);
      me.classed('transitioning', false);
      if (me.classed('exiting')) {
        me.remove();
      }
    });

  const dayGraphs = merged.selectAll('g.day')
    .data(days);

  dayGraphs.exit()
    .classed('exiting', true)
  .transition(t)
    .on('interrupt end', remove);

  const dayGraphsEnter = dayGraphs.enter().append('g')
    .attr('id', d => `day${d}`)
    .attr('class', 'day');

  // const dayGraph = dayGraphs.merge(dayGraphsEnter).filter(d => d === day);
  const dayGraph = dayGraphs.merge(dayGraphsEnter).filter(d => !day || (d === day));

  if (!sgv.length && !smbg.length) {
    // No data yet...
    return;
  }
  
  const sensorCirclesG = dayGraph.selectAll('g.sensor.circles')
    .data((d, i) => [{ day: d, i }]);

  const sensorCirclesGEnter = sensorCirclesG.enter().append('g')
    .attr('id', d => d.day)
    .attr('class', 'sensor circles')
    .attr('transform', d => `translate(${d.i * -width},0)`)
    .style('shape-rendering', 'auto');

  sensorCirclesGEnter.selectAll('circle.smbg')
    .data(smbg, idKey)
    .enter().insert('circle', 'circle.sgv')
      .attr('class', getSmbgClass)
      .attr('r', 1)
      .attr('cx', timeScale)
      .attr('cy', glucoseScale)
      .call(enter => {
        enter.transition(t)
          .attr('r', 3);
        enter.append('title')
          .text(d => formatGlucose(d.glucose));
      });

  sensorCirclesGEnter.selectAll('circle.sgv')
    .data(sgv, idKey)
    .enter().append('circle')
      .attr('class', getSgvClass)
      .attr('r', 1)
      .attr('cx', timeScale)
      .attr('cy', (pathPoints && pathPoints.length)
        ? getCircleYfromPathpoints(pathPoints, translateX, timeScale, glucoseScale)
        : glucoseScale)
      .call(enter => {
        enter.transition(t)
          .attr('cy', glucoseScale);
        enter.append('title')
          .text(d => formatGlucose(d.glucose));
      });

  sensorCirclesG.exit().remove();

  graphsG.selectAll('path.sensor')
    .data([])
    .exit()
    .call(exit => {
      exit.transition(t)
        .style('opacity', 0)
        .on('interrupt end', remove);
    });
}

const GlucoseDayGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default GlucoseDayGraph;
